import { Button, Checkbox, Dialog, SearchBar, useToast } from '@ordr-as/umami-react'
import { useMemo, useState } from 'react'
import { FavritApp, PaymentProvider } from '../../generated-api'
import { useAllLocations, useEnsurePaymentMethodForLocation } from 'hooks/usePaymentMethodApi'
import { mapFavritAppToName } from 'utils/favritAppUtils'

type AddLocationDialogProps = {
    paymentProvider: PaymentProvider
    paymentMethod: string
    dialogIsOpen: boolean
    onClose: () => void
}

const AVAILABLE_APPS = [
    FavritApp.ADMIN_APP,
    FavritApp.EPOS_APP_DIRECT_SALE,
    FavritApp.EPOS_APP_OPEN_BILL,
    FavritApp.UNIBILL_APP,
    FavritApp.GUEST_APP,
]

const AddLocationDialog = (props: AddLocationDialogProps) => {
    const { paymentProvider, paymentMethod, dialogIsOpen, onClose } = props
    const toast = useToast()

    const [searchQuery, setSearchQuery] = useState('')
    const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null)
    const [selectedLocationName, setSelectedLocationName] = useState<string>('')
    const [enabledInApps, setEnabledInApps] = useState<Set<FavritApp>>(new Set([FavritApp.ADMIN_APP]))

    const { data: allLocations, isLoading: isLoadingLocations } = useAllLocations()

    // Filter locations client-side
    const filteredLocations = useMemo(() => {
        if (!allLocations || searchQuery.trim().length === 0) {
            return []
        }

        const searchLower = searchQuery.trim().toLowerCase()
        return allLocations.filter(
            (location) =>
                location.locationId.toString().includes(searchLower) ||
                location.locationName.toLowerCase().includes(searchLower)
        )
    }, [allLocations, searchQuery])

    const { mutate: runEnsurePaymentMethodForLocation, isLoading: isAdding } = useEnsurePaymentMethodForLocation(
        paymentProvider,
        paymentMethod
    )

    const handleSelectLocation = (locationId: number, locationName: string) => {
        setSelectedLocationId(locationId)
        setSelectedLocationName(locationName)
        setSearchQuery('')
    }

    const handleToggleApp = (app: FavritApp) => {
        const updated = new Set(enabledInApps)
        if (updated.has(app)) {
            updated.delete(app)
        } else {
            updated.add(app)
        }
        setEnabledInApps(updated)
    }

    const handleAdd = () => {
        if (selectedLocationId === null) {
            toast.pushError({
                id: 'add-location-no-selection',
                message: 'Please select a location',
            })
            return
        }

        runEnsurePaymentMethodForLocation(
            {
                locationId: selectedLocationId,
                enabledInApps: enabledInApps,
                overridableConfiguration: undefined,
            },
            {
                onSuccess: () => {
                    toast.pushSuccess({
                        id: 'add-location-success',
                        message: `Successfully added payment method to location ${selectedLocationName}`,
                    })
                    handleClose()
                },
                onError: (error) => {
                    console.error('Failed to add payment method to location:', error)
                    toast.pushError({
                        id: 'add-location-error',
                        message: 'Failed to add payment method to location',
                    })
                },
            }
        )
    }

    const handleClose = () => {
        setSearchQuery('')
        setSelectedLocationId(null)
        setSelectedLocationName('')
        setEnabledInApps(new Set([FavritApp.ADMIN_APP]))
        onClose()
    }

    return (
        <>
            {dialogIsOpen && (
                <Dialog
                    title="Add Payment Method to Location"
                    onClose={handleClose}
                    actionLabel={isAdding ? 'Adding...' : 'Add'}
                    onAction={handleAdd}
                    secondaryActionLabel="Cancel"
                    onSecondaryAction={handleClose}
                    isDisabledAction={selectedLocationId === null || isAdding}
                    isDisabledSecondaryAction={isAdding}
                >
                    <div className="flex flex-col gap-4">
                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Search Location
                            </label>
                            {selectedLocationId === null ? (
                                <>
                                    <SearchBar
                                        value={searchQuery}
                                        placeholderText="Search by location ID or name"
                                        aria-label="Search locations"
                                        setValue={(text) => setSearchQuery(text)}
                                        setActive={() => null}
                                        fullWidth
                                    />
                                    {searchQuery.length > 0 && (
                                        <div className="mt-2 border border-grey-light rounded max-h-48 overflow-y-auto">
                                            {isLoadingLocations && (
                                                <div className="p-3 text-small text-grey-dark">Loading...</div>
                                            )}
                                            {!isLoadingLocations && filteredLocations.length === 0 && (
                                                <div className="p-3 text-small text-grey-dark">No locations found</div>
                                            )}
                                            {!isLoadingLocations &&
                                                filteredLocations.map((location) => (
                                                    <button
                                                        key={location.locationId}
                                                        className="w-full p-3 text-left hover:bg-purple-lightest border-b border-grey-lightest last:border-b-0"
                                                        onClick={() =>
                                                            handleSelectLocation(
                                                                location.locationId,
                                                                location.locationName
                                                            )
                                                        }
                                                    >
                                                        <div className="font-medium">{location.locationName}</div>
                                                        <div className="text-small text-grey-dark">
                                                            ID: {location.locationId} • {location.countryCode}
                                                        </div>
                                                    </button>
                                                ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex items-center gap-2 p-3 bg-purple-lightest rounded">
                                    <div className="flex-1">
                                        <div className="font-medium">{selectedLocationName}</div>
                                        <div className="text-small text-grey-dark">ID: {selectedLocationId}</div>
                                    </div>
                                    <Button kind="tertiary" size="small" onClick={() => setSelectedLocationId(null)}>
                                        Change
                                    </Button>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Enabled in Apps
                            </label>
                            <div className="flex flex-col gap-2">
                                {AVAILABLE_APPS.map((app) => (
                                    <Checkbox
                                        key={app}
                                        isSelected={enabledInApps.has(app)}
                                        onChange={() => handleToggleApp(app)}
                                    >
                                        {mapFavritAppToName(app)}
                                    </Checkbox>
                                ))}
                            </div>
                        </div>
                    </div>
                </Dialog>
            )}
        </>
    )
}

export default AddLocationDialog
